import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useGetList, useGetOne } from "react-admin";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Stack, Typography } from "@mui/material";
import { IPermissions } from "@/types/keycloak";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { useTimeZoneService } from "@/components/timezones/timeZoneService";
import { useTimeZoneContext } from "@/components/timezones/useTimeZoneContext";
import { GET_SCHEDULED_OUTCOMES } from "@/queries/Lifecycle";
import { RetainedSignedCloses } from "@/components/timezones/RetainedSignedCloses";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
import { ETransition, ScheduledTransitionCard } from "./ScheduledTransitionCard";
var TRANSITIONS = {
    "open-voting": ETransition.OPENED,
    "close-voting": ETransition.CLOSED,
};
/**
 * The scheduled openings and closings that fired at `electionId`: its own rows
 * and the event-wide ones, read from each row's `fired_outcome`.
 */
export var firedAtElection = function (events, electionId) {
    return events
        .flatMap(function (event) {
        var _a, _b;
        var fired = (_a = event.annotations) === null || _a === void 0 ? void 0 : _a.fired_outcome;
        if (!fired)
            return [];
        var post = fired.posts.find(function (entry) { return entry.election_id === electionId; });
        var transition = post ? TRANSITIONS[post.action] : undefined;
        return post && transition
            ? [{ id: String(event.id), transition: transition, at: (_b = post.fired_at) !== null && _b !== void 0 ? _b : fired.at, post: post }]
            : [];
    })
        .sort(function (a, b) { return Date.parse(b.at) - Date.parse(a.at); });
};
/** Only the same signed close record can replace the retained processing card. */
export var matchesRetainedClose = function (close, post) {
    var _a;
    return post.action === "close-voting" &&
        post.outcome === "runs" &&
        post.scheduled_event_id === close.scheduled_event_id &&
        post.election_id === close.election_id &&
        post.fingerprint === close.fingerprint &&
        ((_a = post.authorized_by) === null || _a === void 0 ? void 0 : _a.request_id) === close.authorized_by.request_id;
};
/** The Publish tab's cards of what the schedule did at an election. */
export var FiredTransitions = function (_a) {
    var _b, _c;
    var electionEventId = _a.electionEventId, electionId = _a.electionId;
    var tenantId = useTenantStore()[0];
    var t = useTranslation().t;
    var aliasRenderer = useAliasRenderer();
    var election = useGetOne("sequent_backend_election", {
        id: electionId,
    }).data;
    var _d = useQuery(GET_SCHEDULED_OUTCOMES, {
        context: { headers: { "x-hasura-role": IPermissions.ELECTION_EVENT_READ } },
        variables: { electionEventId: electionEventId },
        skip: !electionEventId,
        pollInterval: 30000,
    }), outcomeData = _d.data, outcomesError = _d.error;
    var service = useTimeZoneService();
    var zones = useTimeZoneContext(electionEventId);
    var data = useGetList("sequent_backend_scheduled_event", {
        pagination: { page: 1, perPage: 9999 },
        filter: {
            tenant_id: tenantId,
            election_event_id: electionEventId,
        },
    }).data;
    var fired = firedAtElection(data !== null && data !== void 0 ? data : [], electionId);
    var retained = ((_c = (_b = outcomeData === null || outcomeData === void 0 ? void 0 : outcomeData.get_scheduled_outcomes) === null || _b === void 0 ? void 0 : _b.retained_closes) !== null && _c !== void 0 ? _c : []).filter(function (close) { return close.election_id === electionId; });
    var fallback = retained.filter(function (close) {
        return !close.fired_at || !fired.some(function (entry) { return matchesRetainedClose(close, entry.post); });
    });
    if (!fired.length && !fallback.length && !outcomesError)
        return null;
    var zone = zones.zoneOf(electionId);
    return (_jsxs(Stack, { spacing: 1, sx: { mb: 2 }, "data-testid": "fired-transitions", children: [_jsx(RetainedSignedCloses, { closes: fallback, zoneOf: zones.zoneOf, nameOf: function () {
                    return election ? aliasRenderer(election) : t("eventsScreen.fields.electionId");
                }, unavailable: !!outcomesError }), fired.map(function (_a) {
                var id = _a.id, transition = _a.transition, at = _a.at, post = _a.post;
                var signed = retained.find(function (close) { return matchesRetainedClose(close, post); });
                return (_jsxs(Stack, { spacing: 0.5, children: [_jsx(ScheduledTransitionCard, { transition: transition, time: service.formatDateTimeZone(at, zone, service.text), post: post, zone: zone }), signed ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("lifecycle.signedClose.signedAt", {
                                time: service.formatDateTimeZone(signed.scheduled_at, zone, service.text),
                            }) })) : null] }, id));
            })] }));
};
